import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/useAuthStore';

interface NotificationOptOutItemProps {
  variant: 'desktop' | 'mobile';
}

/**
 * 알림(문자) 수신 설정 토글.
 * 끄면 notification_disabled_users에 기록되어 마케팅 문자 발송 대상에서 제외되고,
 * 다시 켜면 해당 기록이 삭제된다.
 * 탈퇴 대기 중(7/14 대표 정책 '탈퇴 신청자 = 알림 차단')이거나 관리자가 끈 경우에는 켤 수 없다 —
 * 서버 RLS(20261022100000)가 지우기를 막고, 화면은 잠긴 상태와 이유를 보여 준다(10/22 82명 빈틈).
 */
export function NotificationOptOutItem({ variant }: NotificationOptOutItemProps) {
  const { user } = useAuthStore();
  const [disabled, setDisabled] = useState<boolean | null>(null);
  const [locked, setLocked] = useState<'deletion' | 'other' | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    const load = async () => {
      const [{ data, error }, { data: pending }] = await Promise.all([
        supabase
          .from('notification_disabled_users')
          .select('user_id, source')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('free_deletion_requests')
          .select('id')
          .eq('user_id', user.id)
          .in('status', ['scheduled', 'processing'])
          .limit(1),
      ]);

      if (error) {
        console.error('알림 수신 설정 조회 실패:', error);
        return;
      }
      setDisabled(!!data);
      setLocked(data && pending?.length ? 'deletion' : data?.source === 'admin_mute' ? 'other' : null);
    };

    load();
  }, [user?.id]);

  const toggle = async () => {
    if (!user?.id || saving || disabled === null || locked) return;

    setSaving(true);
    const next = !disabled;
    setDisabled(next);

    try {
      if (next) {
        const phoneNumber = user.phone || user.user_metadata?.phone || null;
        const { error } = await supabase
          .from('notification_disabled_users')
          .upsert(
            { user_id: user.id, phone_number: phoneNumber, disabled_at: new Date().toISOString() },
            { onConflict: 'user_id' }
          );
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('notification_disabled_users')
          .delete()
          .eq('user_id', user.id)
          .select('user_id');
        if (error) throw error;
        // 서버가 지우기를 막으면(탈퇴 대기·관리자 끔) 지워진 줄이 0 — 켜진 것처럼 보이지 않게 되돌린다
        if (!data?.length) {
          setDisabled(true);
          setLocked('other');
        }
      }
    } catch (error) {
      console.error('알림 수신 설정 변경 실패:', error);
      setDisabled(!next);
    } finally {
      setSaving(false);
    }
  };

  if (!user || disabled === null) return null;

  const label = locked ? '알림 꺼짐' : disabled ? '알림 받기' : '알림 끄기';
  const description = locked === 'deletion'
    ? '탈퇴 신청 중에는 켤 수 없습니다. 탈퇴를 취소하면 알림을 받을 수 있습니다.'
    : locked === 'other'
      ? '지금은 켤 수 없습니다. 고객센터로 문의해 주세요.'
      : disabled ? '문자 수신이 꺼져 있습니다' : '문자로 소식을 받고 있습니다';
  const Icon = disabled ? Bell : BellOff;

  if (variant === 'desktop') {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={saving || !!locked}
        className="group flex items-center gap-3 px-3 py-2.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] rounded-xl hover:bg-[#27272A]/50 transition-all duration-300 w-full disabled:opacity-50"
      >
        <div className="w-8 h-8 rounded-lg bg-[#27272A] group-hover:bg-[#3F3F46] flex items-center justify-center transition-colors duration-300">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 text-left">
          <span className="font-medium block">{label}</span>
          <span className="text-[11px] text-[#71717A] break-keep">{description}</span>
        </div>
        <span
          className={`relative w-9 h-5 rounded-full transition-colors duration-300 ${
            disabled ? 'bg-[#3F3F46]' : 'bg-[#3182f6]'
          }`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300 ${
              disabled ? 'left-0.5' : 'left-[18px]'
            }`}
          />
        </span>
      </button>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={toggle}
      disabled={saving || !!locked}
      whileTap={{ scale: 0.98 }}
      className="group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-[#8b95a1] hover:text-white hover:bg-[#1a1a1a] w-full disabled:opacity-50"
    >
      <div className="w-8 h-8 rounded-lg bg-[#1a1a1a] border border-[#333d4b] flex items-center justify-center transition-all duration-200">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 text-left">
        <span className="text-[14px] font-medium block">{label}</span>
        <span className="text-[11px] text-[#6b7684] break-keep">{description}</span>
      </div>
      <span
        className={`relative w-9 h-5 rounded-full transition-colors duration-300 ${
          disabled ? 'bg-[#333d4b]' : 'bg-[#3182f6]'
        }`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300 ${
            disabled ? 'left-0.5' : 'left-[18px]'
          }`}
        />
      </span>
    </motion.button>
  );
}
